import { Children, useDeferredValue, useEffect, useState } from "react";
import { AddIcon, GlossaryIcon, TagsIcon } from "allIcons";
import {
  BodyContainer,
  FiltersList,
  HeaderContainer,
  InputField,
  ListCard,
  ListIconWrapper,
  Loader,
  LoadingListCards,
  MainButton,
  PrimaryHeading,
  RightMainContainer,
  TemplateNotFound,
} from "commonUI";
import {
  useGlosssary,
  useAddGlossary,
  useDeleteGlossary,
  useGetGlossaryCategory,
} from "api/templateLibrary/glossary";
import { Link } from "react-router-dom";
import { Modules, parse, search } from "helpers";
import { useTags } from "api/siteSettings/Tags";
import {
  libraryItemFiltering,
  libraryItemSorting,
} from "filterAndSort/templateLibrary";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function Glossary() {
  const { data: glossary, isLoading } = useGlosssary();
  const { mutate: addGlossary, isLoading: isAdding } = useAddGlossary(false);
  const { mutate: deleteGlossary, isLoading: isDeleting } =
    useDeleteGlossary(false);
  const { data: categories } = useGetGlossaryCategory();

  const { data: tags } = useTags();
  const [searchText, setSearchText] = useState("");
  const defferedSearch = useDeferredValue(searchText);
  const [searchResult, setSearchResult] = useState([]);
  const [filterData, setFilterData] = useState({});
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();

  useEffect(() => {
    setSearchResult(
      glossary.filter((task) => search(task.glossary_title, defferedSearch))
    );
  }, [defferedSearch, glossary]);

  const applySorting = (order, orderBy) => {
    setSearchResult((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult, "glossary_title")
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setSearchResult(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        glossary,
        "category_id"
      )
    );
  };

  return (
    <>
      <RightMainContainer>
        {(isAdding || isDeleting) && <Loader loader="block" />}
        <HeaderContainer>
          <PrimaryHeading>{t("libraryTemplate.glossary.title")}</PrimaryHeading>
          <div className="flex items-center gap-7">
            {add_new_templates ? (
              <Link to="add">
                <MainButton>
                  <AddIcon />
                  {t("button.add")}
                </MainButton>
              </Link>
            ) : (
              ""
            )}
            <Link to="categories">
              <MainButton>
                <TagsIcon />
                {t("libraryTemplate.glossary.glossaryCategories")}
              </MainButton>
            </Link>
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon={true}
            filter={true}
            value={searchText}
            setValue={(val) => setSearchText(val)}
            tags={tags ?? []}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterData={filterData}
            showLibrarySource
            filterAddTitle={t(
              "libraryTemplate.glossary.filter.category.addCategory"
            )}
            filterTitle={t("libraryTemplate.glossary.filter.category.title")}
            filterList={categories?.map((item) => ({
              title: `${item.title}`,
              id: item.id,
              icon: <ListIconWrapper>{<TagsIcon />}</ListIconWrapper>,
              module: Modules.CATEGORY,
              category_id: item.id,
            }))}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {isLoading ? (
            <LoadingListCards />
          ) : searchResult.length === 0 ? (
            <TemplateNotFound
              label={t("libraryTemplate.glossary.title")}
              link={add_new_templates ? "add" : ""}
              linkLabel={t("libraryTemplate.glossary.glossaryItem")}
            />
          ) : (
            Children.toArray(
              searchResult?.map((ele, index) => {
                return (
                  <ListCard
                    data={ele.glossary_title}
                    secondaryData={ele?.category?.title || ""}
                    duplicate={add_new_templates}
                    editIcon={edit_templates}
                    link={
                      edit_templates
                        ? `/${ModulePrefixes.TEMPLATE_LIBRARY}/glossary/${ele.id}`
                        : ""
                    }
                    strictLink={true}
                    deleteIcon={!delete_templates}
                    onDuplicate={() => {
                      addGlossary({
                        glossary_title: `Copy of ${ele.glossary_title}`,
                        glossary_body: ele.glossary_body,
                        glossary_integration: ele.glossary_integration,
                        tags: parse(ele.tags),
                      });
                    }}
                    onDelete={() => deleteGlossary(ele.id)}
                    icon={
                      <ListIconWrapper>
                        <GlossaryIcon fill="white" />
                      </ListIconWrapper>
                    }
                    deleteTitle={t("libraryTemplate.delete.title")}
                    deleteButtonText={t("libraryTemplate.delete.button")}
                    deleteMessage={t("libraryTemplate.delete.message")}
                  />
                );
              })
            )
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
